import React from "react";
import {InspectionItemContainer, StyledTextArea, SubQuestionWrapper} from "../styles";
import {Box, Checkbox, Fade, FormControlLabel, FormGroup, MenuItem, Tooltip, Typography} from "@mui/material";
import checkmark from "assets/animations/Checkmark.json";
import Lottie from "react-lottie";
import {Info, PersonSearch} from "@mui/icons-material";

/**
 * Takes a string and returns the string with the first letter capitalized and all other letters in lowercase.
 * This function is typically used to format display text to ensure consistency, especially when the input string
 * might have irregular casing.
 * @param {string} string - The input string to be formatted.
 * @returns {string} The formatted string with only the first letter capitalized and all other letters in lowercase.
 */
function capitalizeFirstLetter(string) {
  const formatted = string.toLowerCase();
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

/**
 * Takes a string and returns it in sentence case.
 * This function ensures the first character of the sentence is capitalized,
 * while the rest of the characters are in lowercase, preserving original spacing.
 * @param {string} sentence - The input string to be converted to sentence case.
 * @returns {string} The formatted string in sentence case.
 */
function toSentenceCase(sentence) {
  if (!sentence || typeof sentence !== "string") {
    return "";
  }

  const trimmed = sentence.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}


/**
 *
 * @param previousOption
 * @param subQuery
 */
function findPreviousComments(previousOption, subQuery) {
  try {
    const item = previousOption?.inspectionSubQueries.filter(item => item.name.toLowerCase() === subQuery.name.toLowerCase());
    return item[0].ratings[0];
  }
  catch (error) {
    return null;
  }
}

/**
 * Renders an individual question item as part of an inspection form.
 * Each question can have multiple rating options and might lead to sub-questions
 * based on the selected rating.
 * @param {object} props - The props for the QuestionItem component.
 * @param {object} props.option - The question data including name, ratings, and any sub-queries.
 * @param {number} props.mainIndex - The index of the main question in the list, used for unique keys and handling changes.
 * @param {Function} props.handleOptionChange - Function to call when an option is selected, updating the state.
 * @param {object} props.selectedOptions - Object tracking all selected options for main and sub-questions.
 * @param {Function} props.handleSubOptionChange - Function to handle changes to sub-question selections.
 * @param {object} props.previousInspection - Object containing the previous inspection data for highlighting.
 * @returns {React.Component}
 */
function QuestionItem({ option, mainIndex, handleOptionChange, selectedOptions, handleSubOptionChange, previousInspection }) {
  const isAnswered =
    selectedOptions[option.name]?.rating !== undefined && (
      !selectedOptions[option.name]?.isParent ||
      (Array.isArray(selectedOptions[option.name]?.inspectionSubQueries?.ratings) && selectedOptions[option.name]?.inspectionSubQueries?.ratings.length > 0) ||
      (Array.isArray(selectedOptions[option.name]?.inspectionSubQueriesText?.ratings) && selectedOptions[option.name]?.inspectionSubQueriesText?.ratings.length > 0)
    );

  // Find previously selected option from the previous inspection
  const previousOption = previousInspection?.inspectionOptions.find(
    (prevOpt) => prevOpt.name.toLowerCase() === option.name.toLowerCase()
  );
  return (
    <Fade in={true} timeout={1000} key={mainIndex}>
      <InspectionItemContainer
        style={{ borderColor: isAnswered ? "forestgreen" : "crimson", borderWidth: isAnswered ? 2 : 2, position: "relative" }}
      >
        {isAnswered && (
          <div style={{ position: "absolute", top: 5, right: 5 }}>
            <Lottie
              width={30}
              height={30}
              options={{
                loop: false,
                autoplay: true,
                animationData: checkmark,
                rendererSettings: {
                  preserveAspectRatio: "xMidYMid slice",
                },
              }}
            />
          </div>
        )}
        <Typography variant="h6">{capitalizeFirstLetter(option.name)}</Typography>
        {option.ratings.map((rating) => {
          // Check if this rating was previously selected
          const isPreviouslySelected = previousOption && previousOption.rating.toLowerCase() === rating.rating.toLowerCase();

          return (
            <MenuItem
              key={rating.rating}
              onClick={() => handleOptionChange(rating.rating, option.name, rating.isParent)}
              selected={selectedOptions[option.name]?.rating === rating.rating}
              // style={{
              //   backgroundColor: isPreviouslySelected ? "rgba(0, 123, 255, 0.1)" : "transparent",
              //   fontWeight: isPreviouslySelected ? "bold" : "normal",
              // }}
            >
              <Box sx={{ display: "flex",  flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <span
                  style={{
                    color: isPreviouslySelected ? "#343434" : "inherit",
                    whiteSpace: "normal", // Enable text wrapping
                    wordBreak: "break-word", // Handle long words that exceed container width
                  }}>{capitalizeFirstLetter(rating.rating)}</span>
                {isPreviouslySelected && <PersonSearch sx={{ color: "#e35205" }} />}
              </Box>
            </MenuItem>
          );
        })}
        {option.ratings.find(
          (r) => r.rating === selectedOptions[option.name]?.rating && r.isParent
        ) &&
          option.inspectionSubQueries.map((subQuery, subIndex) => {
            return (
              <React.Fragment key={subIndex}>
                <Typography variant="subtitle1" style={{ marginTop: 25 }}>
                  {capitalizeFirstLetter(subQuery.name)}
                </Typography>
                {subQuery.selectType === "SELECT MANY" || subQuery.selectType === "SELECT ONE" ? (
                  <SubQuestionWrapper>
                    <FormGroup>
                      {subQuery.ratings.map((subRating) => {
                        const prev_selected = previousOption?.inspectionSubQueries[0]?.ratings
                          ?.map(rating => rating.toLowerCase())
                          .includes(subRating.rating.toLowerCase());

                        return (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              flex: 1,
                              alignItems: "center",
                              gap: 1
                            }}
                            key={`${subQuery.name}-${subRating.rating}`} // Ensure the key is unique
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    selectedOptions[option.name]?.inspectionSubQueries?.ratings?.includes(subRating.rating) ||
                                    false
                                  }
                                  onChange={() =>
                                    handleSubOptionChange({
                                      rating: subRating.rating,
                                      name: subQuery.name,
                                      parent: option.name,
                                      type: subQuery.selectType,
                                    })
                                  }
                                  style={{
                                    color:
                                      prev_selected
                                        ? "#343434"
                                        : "default",
                                  }}
                                />
                              }
                              label={capitalizeFirstLetter(subRating.rating)}
                            />

                            {subRating?.description && (
                              <Tooltip title={subRating?.description} arrow>
                                <Info sx={{ color: "#6e6e6e" }} />
                              </Tooltip>
                            )}

                            {prev_selected && (
                              <Tooltip title="Customer selection" arrow>
                                <PersonSearch sx={{ color: "#e35205" }} />
                              </Tooltip>
                            )}
                          </Box>
                        );
                      })}
                    </FormGroup>

                  </SubQuestionWrapper>
                ) : (
                  <>
                    <StyledTextArea
                      fullWidth
                      select
                      minRows={5}
                      label={subQuery.name}
                      value={selectedOptions[option.name].inspectionSubQueriesText?.ratings[0]}
                      onChange={(e) =>
                        handleSubOptionChange({
                          rating: e.target.value,
                          name: subQuery.name,
                          parent: option.name,
                          type: subQuery.selectType,
                        })
                      }
                    />
                    {findPreviousComments(previousOption, subQuery) &&
                      <Box>
                        <PersonSearch sx={{ color: "#e35205" }} />
                        <Typography sx={{ fontStyle: "italic", color: "#9d9d9d" }}>{findPreviousComments(previousOption, subQuery)}</Typography>
                      </Box>}
                  </>
                )}
              </React.Fragment>
            );
          })}
      </InspectionItemContainer>
    </Fade>
  );
}

export default QuestionItem;
